import { Doc } from '@dadohr/shared/domainTypes/document';
import { UserField } from '@dadohr/shared/domainTypes/experience';
import {
  IntegrationDataMap,
  MergeHRISIntegration
} from '@dadohr/shared/domainTypes/integrations/merge';
import {
  CustomFieldSmartDate,
  CustomFieldSmartText,
  CustomFieldUserRelationship,
  CustomUserField,
  IOrganization
} from '@dadohr/shared/domainTypes/org';
import { Operator, Rule } from '@dadohr/shared/domainTypes/rules';
import { findSmartTextCyclePath } from '@dadohr/shared/domainTypes/smartFields';
import {
  BUILTIN_USER_DATE_FIELDS,
  CALCULATED_FIELDS,
  CalculatedFieldDefinition,
  CustomIdField,
  IUser
} from '@dadohr/shared/domainTypes/user';
import { isValueOfEnum } from '@dadohr/shared/domainTypes/utility';
import { FS } from '@dadohr/shared/versions';
import { css } from '@emotion/css';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@material-ui/core';
import { assertNever } from 'assert-never';
import { camelCase, compact, flatten } from 'lodash';
import { Edit, Eye, Plus, PlusCircle, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { AutocompleteSingle } from '../../../../components/Autocomplete';
import { ButtonWithPromise } from '../../../../components/ButtonWithPromise';
import { DeleteButton } from '../../../../components/DeletionConfirmation';
import { DialogActionsWithSlots } from '../../../../components/DialogActionsWithSlots';
import { DialogTitleWithTypography } from '../../../../components/DialogTitleWithTypography';
import { FormRow } from '../../../../components/Form/FormRow';
import {
  ItemSorter,
  ItemSorters,
  ROW_HEIGHTS,
  RowsRenderer
} from '../../../../components/GroupableList';
import { NoAccess } from '../../../../components/NoAccess';
import { IColumn } from '../../../../components/Table/VirtualizedSortable';
import { SelectBeforeAfter } from '../../../../components/Time/SelectBeforeAfter';
import { useDialogState } from '../../../../hooks/useDialogState';
import { useSnackbar } from '../../../../hooks/useSnackbar';
import { useSort } from '../../../../hooks/useSort';
import { UnsafeTFunction, useUnsafeTranslation } from '../../../../i18next';
import { Centered } from '../../../../layout/Centered';
import { PageBody } from '../../../../layout/PageBody';
import {
  PageToolbar,
  PageToolbarActions,
  PageToolbarTitle
} from '../../../../layout/PageToolbar';
import { Section } from '../../../../layout/Section';
import { useRoutes, useStringQueryParam } from '../../../../routes';
import { useCurrentUser } from '../../../../services/currentUser';
import { updateDocFromPartial } from '../../../../services/firebase';
import { lightMdToJsx } from '../../../../services/formatter';
import { hasPermissionToAccessPage } from '../../../../services/permissions';
import { useUserRuleConfigWithAutocompletion, useUsersByOrgId } from '../../../../services/user';
import { useTheme } from '../../../../themes';
import { RuleBuilder } from '../../../Experiences/components/RuleBuilder';
import { MatchingUsers } from '../../../Experiences/pages/TemplateEdit/StepParticipants';
import { useConfigInfos } from '../Integrations/integrations/common';
import { useOrgSideNav } from '../sideNav';

type ColumnName = 'label' | 'description' | 'type' | 'key' | 'actions';
type D = CustomUserField;
type O = undefined;
type Column = IColumn<D, ColumnName, O>;

const Actions = ({ d }: { d: D }) => {
  if (d.restricted) {
    return null;
  }
  return (
    <IconButton>
      <Edit size={16} />
    </IconButton>
  );
};

// @guide Add a smart field - 2.1 Make it appear on the Data Fields screen
//
// We want to show these calculated/computed properties on the Data FIelds screen for transparency.
// Add the translations necessary here and/or in the translations file.
const calculatedFieldToCustomUserFields = (
  field: CalculatedFieldDefinition,
  t: UnsafeTFunction
): CustomUserField[] => {
  // Strictly speaking a smart field is not a custom user field -
  // for the purpose of this screen we treat them like this though and do a
  // simple transformation from one to the other so that we can display them here.
  if (field.type === 'recurringDate') {
    if (field.key === 'birthday') {
      return [
        {
          type: 'date',
          key: `${field.key}-date`, // as this key is also shown in the UI, this is a bit misleading
          label: t('Next Birthday'),
          description: t('calculatedFields.birthday.description.date'),
          restricted: true,
          hideYear: false
        }
      ];
    }
    return compact([
      {
        type: 'date',
        key: `${field.key}-date`, // as this key is also shown in the UI, this is a bit misleading
        label: `${field.label} ${t('Date')}`,
        description:
          field.key === 'anniversary'
            ? t('calculatedFields.anniversary.description.date')
            : '',
        restricted: true,
        hideYear: false
      },
      field.exposeCount && {
        type: 'number',
        key: `${field.key}-count`, // as this key is also shown in the UI, this is a bit misleading
        label: `${field.label} ${t('Count')}`,
        description:
          field.key === 'anniversary'
            ? t('calculatedFields.anniversary.description.count')
            : '',
        restricted: true
      }
    ]);
  }

  return [];
};

const calculatedFieldsToCustomUserFields = (
  fields: CalculatedFieldDefinition[],
  t: UnsafeTFunction
) => {
  return flatten(fields.map((f) => calculatedFieldToCustomUserFields(f, t)));
};

export const COLUMNS: Column[] = [
  {
    key: 'label',
    head: () => 'Name',
    cell: (d) => {
      return d.label;
    },
    align: 'left',
    sortable: true,
    width: 120,
    flexGrow: 1
  },
  {
    key: 'key',
    head: () => 'Data key',
    cell: (d) => {
      return d.key;
    },
    align: 'left',
    sortable: true,
    width: 120,
    flexGrow: 1
  },
  {
    key: 'description',
    head: () => 'Description',
    cell: (d) => {
      return d.description;
    },
    align: 'left',
    sortable: true,
    width: 120,
    flexGrow: 2,
    showOverflowOnHover: false
  },
  {
    key: 'actions',
    head: () => '',
    alternateHead: () => 'Actions',
    cell: (d) => {
      return <Actions d={d} />;
    },
    align: 'right',
    sortable: true,
    width: 50,
    flexGrow: 1,
    showOverflowOnHover: false
  }
];

export const SORTERS: ItemSorters<D, O> = {
  label: {
    key: 'label',
    items: {
      sort: (d) => d.label?.toLowerCase() || '',
      dir: 'asc'
    }
  }
};
const ROW_TO_KEY = (d: D) => d.key;

const STANDARD_USER_KEYS: (keyof IUser)[] = [
  'alternateId',
  'firstName',
  'middleName',
  'lastName',
  'preferredName',

  'gender',
  'email',
  'personalEmail',

  'managerId',
  'orgId',

  'role',
  'location',
  'department',
  'team',

  'hireDate',
  'terminationDate',

  'active'
];

// Keys that are not stored on IUser but are either computed at read time
// (e.g. `fullName` from firstName + lastName) or registered as built-in/calculated
// fields. Allowing an admin to create a custom field with one of these keys
// silently shadows the computed value and breaks downstream consumers.
const RESERVED_DERIVED_KEYS: string[] = [
  'fullName',
  ...BUILTIN_USER_DATE_FIELDS.map((f) => f.key),
  ...CALCULATED_FIELDS.map((f) => f.key)
];

const DEFAULT_USER_FIELDS: CustomUserField[] = [
  {
    key: 'firstName',
    label: 'First Name',
    description: 'This is a default field.',
    type: 'string',
    restricted: true
  },
  {
    key: 'preferredName',
    label: 'Preferred Name',
    description: 'This is a default field.',
    type: 'string',
    restricted: true
  },
  {
    key: 'lastName',
    label: 'Last Name',
    description: 'This is a default field.',
    type: 'string',
    restricted: true
  },
  {
    key: 'middleName',
    label: 'Middle Name',
    description: 'This is a default field.',
    type: 'string',
    restricted: true
  },
  {
    key: 'gender',
    label: 'Gender',
    description: 'This is a default field.',
    type: 'string',
    restricted: true
  },
  {
    key: 'email',
    label: 'Work Email',
    description:
      'This is a default field. Users cannot have the same Work Email as each other.',
    type: 'string',
    restricted: true
  },
  {
    key: 'personalEmail',
    label: 'Personal Email',
    description:
      'This is a default field. Users cannot have the same Personal Email as each other.',
    type: 'string',
    restricted: true
  },
  {
    key: 'managerId',
    label: 'Manager',
    description: "This is a default field, which identifies a user's manager.",
    type: 'relationship',
    identifyBy: 'email',
    restricted: true
  },
  {
    key: 'role',
    label: 'Role',
    description: 'This is a default field.',
    type: 'string',
    restricted: true
  },
  {
    key: 'team',
    label: 'Team',
    description: 'This is a default field.',
    type: 'string',
    restricted: true
  },
  {
    key: 'department',
    label: 'Department',
    description: 'This is a default field.',
    type: 'string',
    restricted: true
  },
  {
    key: 'location',
    label: 'Location',
    description: 'This is a default field.',
    type: 'string',
    restricted: true
  },
  {
    key: 'hireDate',
    label: 'First Day at Work',
    description:
      'This is a default field. Anniversary Date and Anniversary Count are calculated based on this field.',
    type: 'date',
    hideYear: false,
    restricted: true
  },
  {
    key: 'terminationDate',
    label: 'Last Day at Work',
    description: 'This is a default field.',
    type: 'date',
    hideYear: false,
    restricted: true
  },
  {
    key: 'dateOfBirth',
    label: 'Date of Birth',
    description:
      'This is a default field. Next Birthday is calculated based on this field.',
    type: 'date',
    hideYear: true,
    restricted: true
  },
  {
    key: 'address',
    label: 'Address',
    description: 'This is a default field.',
    type: 'address',
    restricted: true
  },
  {
    key: 'alternateId',
    label: 'Alternate ID',
    description:
      'This is a default field, used to store an ID from other systems, eg your HRIS.',
    type: 'string',
    restricted: true
  },
  {
    key: 'phoneNumber',
    label: 'Phone Number',
    description: 'This is a default field.',
    type: 'string',
    restricted: true
  }
];

const isKeyUnique = (v: CustomUserField, otherFields: CustomUserField[]) => {
  const allKeys = new Set<string>([
    ...STANDARD_USER_KEYS,
    ...RESERVED_DERIVED_KEYS,
    ...otherFields.map((f) => f.key)
  ]);
  return !allKeys.has(v.key);
};

const hasMissingFields = (v: CustomUserField) => {
  if (v.type === 'relationship') return !v.label || !v.identifyBy;
  if (v.type === 'smartDate') return !v.label || !v.sourceField || !v.offset || !v.direction;
  if (v.type === 'smartText') return !v.label || !v.options.length || v.options.some(o => !o.value);
  return !v.label;
};

const TYPES: CustomUserField['type'][] = [
  'string',
  'number',
  // 'boolean',
  'date',
  'address',
  'relationship',
  'smartDate',
  'smartText'
];

const typeToString = (t: UnsafeTFunction, type: CustomUserField['type']) => {
  switch (type) {
    case 'string':
      return t('Text');
    case 'number':
      return t('Number');
    case 'boolean':
      return t('Boolean (yes/no)');
    case 'date':
      return t('Date');
    case 'relationship':
      return t('User relationship');
    case 'address':
      return t('Address');
    case 'smartDate':
      return t('Smart field – date');
    case 'smartText':
      return t('Smart field – text');
    default:
      return assertNever(type);
  }
};

const TypeSelector = ({
  value,
  onChange,
  restrictEdits
}: {
  value: CustomUserField;
  onChange: (nextValue: CustomUserField) => void;
  restrictEdits?: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  const labelId = 'custom-user-field-type-selector';
  return (
    <FormControl variant="outlined" fullWidth>
      <InputLabel id={labelId}>{t('Data Type')}</InputLabel>
      <Select
        labelId={labelId}
        value={value.type}
        onChange={(ev) => {

          const nextType = ev.target.value as CustomUserField['type'];
          if (nextType === 'relationship') {
            onChange({
              ...value,
              type: nextType,
              identifyBy: ''
            });
          } else if (nextType === 'date') {
            onChange({
              ...value,
              type: nextType,
              hideYear: false // We coulod ad the possibility of custom years without year later in the UI
            });
          } else if (nextType === 'smartDate') {
            onChange({
              ...value,
              type: nextType,
              sourceField: '',
              offset: 1,
              direction: 'after'
            });
          } else if (nextType === 'smartText') {
            onChange({
              ...value,
              type: nextType,
              options: [{ value: '', rule: EMPTY_SMART_TEXT_RULE }]
            });
          } else {
            onChange({
              ...value,
              type: nextType
            });
          }
        }}
        disabled={restrictEdits}
        fullWidth
      >
        {TYPES.map((type) => (
          <MenuItem key={type} value={type}>
            {typeToString(t, type)}
          </MenuItem>
        ))}
      </Select>
      {value.type === 'smartDate' && (
        <FormHelperText style={{ color: 'black' }}>
          {t('Set a dynamic date based off another date, e.g. "Training Start Date" is 3 days after first day at work')}
        </FormHelperText>
      )}
      {value.type === 'smartText' && (
        <FormHelperText style={{ color: 'black' }}>
          {t('Set the value of this field based on other fields, e.g. set a Region based on the value in the State field')}
        </FormHelperText>
      )}
    </FormControl>
  );
};

const FieldFormCommon = ({
  value,
  onChange,
  restrictEdits
}: {
  value: Exclude<CustomUserField, CustomFieldUserRelationship>;
  onChange: (nextValue: CustomUserField) => void;
  restrictEdits?: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <>
      <FormRow>
        <TextField
          label={t('Name')}
          value={value.label}
          onChange={(ev) =>
            onChange({
              ...value,
              label: ev.target.value
            })
          }
          fullWidth
        />
        <TextField
          label={t('Data key')}
          value={value.key || camelCase(value.label)}
          disabled={restrictEdits}
          onChange={(ev) =>
            onChange({
              ...value,
              key: ev.target.value
            })
          }
          fullWidth
        />
      </FormRow>
      <FormRow>
        <TypeSelector
          value={value}
          onChange={onChange}
          restrictEdits={restrictEdits}
        />
      </FormRow>
      <FormRow>
        <TextField
          label={t('Description (optional)')}
          value={value.description}
          rows={2}
          multiline
          onChange={(ev) =>
            onChange({
              ...value,
              description: ev.target.value
            })
          }
          fullWidth
        />
      </FormRow>
    </>
  );
};
type Option = { key: string; label: string };
const isCutomIdField = isValueOfEnum(CustomIdField);
const FieldFormRelationship = ({
  value,
  onChange,
  restrictEdits
}: {
  value: CustomFieldUserRelationship;
  onChange: (nextValue: CustomUserField) => void;
  restrictEdits?: boolean;
}) => {
  const { t } = useUnsafeTranslation();

  const user = useCurrentUser();
  const [configInfosDoc] = useConfigInfos(user.org.id);
  if (!configInfosDoc) {
    return null;
  }

  const customFields: Option[] = user.org.customUserFields
    .filter((f) => isCutomIdField(f.key))
    .map((userField) => {
      if (userField.key === CustomIdField.MERGE) {
        const mergeconfig = configInfosDoc.data.configInfos.merge;
        if (!mergeconfig || !mergeconfig.activeHRIS) {
          return { key: userField.key, label: userField.label };
        }
        const mergeHris = mergeconfig.activeHRIS[0]; // This assumes there is only one Merge integration. Thats ok... for now
        const label = `${
          IntegrationDataMap[mergeHris as MergeHRISIntegration].name
        } ID`;
        return { key: userField.key, label };
      }
      return { key: userField.key, label: userField.label };
    });

  const identifyByOptions: Option[] = [
    { key: 'email', label: 'Email' },
    { key: 'personalEmail', label: 'Personal Email' },
    { key: 'alternateId', label: 'Alternate ID' },
    ...customFields
  ];

  return (
    <>
      <FormRow>
        <TextField
          label={t('Label')}
          value={value.label}
          onChange={(ev) =>
            onChange({
              ...value,
              label: ev.target.value
            })
          }
          fullWidth
        />
        <TextField
          label={t('Data Key')}
          value={value.key || camelCase(value.label)}
          disabled={restrictEdits}
          onChange={(ev) =>
            onChange({
              ...value,
              key: ev.target.value
            })
          }
          fullWidth
        />
      </FormRow>
      <FormRow>
        <TypeSelector
          value={value}
          onChange={onChange}
          restrictEdits={restrictEdits}
        />
      </FormRow>
      <FormRow>
        <TextField
          label={t('Description')}
          value={value.description}
          rows={2}
          multiline
          onChange={(ev) =>
            onChange({
              ...value,
              description: ev.target.value
            })
          }
          fullWidth
        />
      </FormRow>
      <FormRow>
        <AutocompleteSingle
          label={t('Identified by')}
          value={identifyByOptions.find((o) => o.key === value.identifyBy)}
          fullWidth
          options={identifyByOptions}
          getOptionLabel={(o) => o?.label}
          onChange={(o) =>
            onChange({
              ...value,
              identifyBy: o?.key || ''
            })
          }
        />
      </FormRow>
    </>
  );
};

const FieldFormSmartDate = ({
  value,
  onChange,
  restrictEdits
}: {
  value: CustomFieldSmartDate;
  onChange: (nextValue: CustomUserField) => void;
  restrictEdits?: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  const theme = useTheme();
  const user = useCurrentUser();
  const sourceDateOptions = [
    ...BUILTIN_USER_DATE_FIELDS.map((f) => ({ key: f.key, label: f.defaultLabel })),
    ...user.org.customUserFields
      .filter((f) => f.type === 'date')
      .map((f) => ({ key: `customFields.${f.key}`, label: f.label }))
  ];

  return (
    <>
      <FormRow>
        <TextField
          label={t('Name')}
          value={value.label}
          onChange={(ev) => onChange({ ...value, label: ev.target.value })}
          fullWidth
        />
        <TextField
          label={t('Data key')}
          value={value.key || camelCase(value.label)}
          disabled={restrictEdits}
          onChange={(ev) => onChange({ ...value, key: ev.target.value })}
          fullWidth
        />
      </FormRow>
      <FormRow>
        <TypeSelector value={value} onChange={onChange} restrictEdits={restrictEdits} />
      </FormRow>
      <FormRow>
        <TextField
          type="number"
          inputProps={{ min: 1, step: 1 }}
          value={value.offset || ''}
          disabled={restrictEdits}
          onChange={(ev) => {
            const n = parseInt(ev.target.value, 10);
            if (!isNaN(n) && n > 0) {
              onChange({ ...value, offset: n });
            }
          }}
          style={{ width: theme.spacing(10) }}
        />
        <Typography variant="body2">
          {t('day')}
        </Typography>
        <SelectBeforeAfter
          value={value.direction === 'before' ? 'bef' : 'aft'}
          onChange={(nextDir) => onChange({ ...value, direction: nextDir === 'bef' ? 'before' : 'after' })}
        />
        <FormControl variant="outlined" style={{ flex: 1 }}>
          <Select
            value={value.sourceField || ''}
            disabled={restrictEdits}
            displayEmpty
            onChange={(ev) => onChange({ ...value, sourceField: ev.target.value as string })}
          >
            <MenuItem value="" disabled>{t('Select source date')}</MenuItem>
            {sourceDateOptions.map((opt) => (
              <MenuItem key={opt.key} value={opt.key}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </FormRow>
      <FormRow>
        <TextField
          label={t('Description (optional)')}
          value={value.description}
          rows={2}
          multiline
          onChange={(ev) => onChange({ ...value, description: ev.target.value })}
          fullWidth
        />
      </FormRow>
    </>
  );
};

const EMPTY_SMART_TEXT_RULE: Rule<UserField> = {
  type: 'all',
  els: [{ k: 'department', op: 'isAnyOf' as Operator, v: [] }]
};

const SmartTextOptionContainer = css({
  marginBottom: 24
});

const SmartTextOptionHeader = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between'
});

const SmartTextFieldValueRow = css({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  marginBottom: 8,
  flex: 1
});

const SmartTextFieldValueLabel = css({
  fontWeight: 400,
  fontSize: 16,
  lineHeight: '24px',
  whiteSpace: 'nowrap',
  flexShrink: 0
});

const FieldFormSmartText = ({
  value,
  onChange,
  restrictEdits
}: {
  value: CustomFieldSmartText;
  onChange: (nextValue: CustomUserField) => void;
  restrictEdits?: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  const user = useCurrentUser();
  const [users, usersLoading] = useUsersByOrgId(user.org.id);
  const [previewOptionIndex, setPreviewOptionIndex] = useState<number | null>(null);
  const fieldsWithoutSmartDate = useMemo(
    () => user.org.customUserFields.filter((f) => f.type !== 'smartDate'),
    [user.org.customUserFields]
  );
  const ruleConfig = useUserRuleConfigWithAutocompletion(
    user.org.id,
    fieldsWithoutSmartDate,
    user.org.tz,
    { orgId: user.org.id, experienceTemplateId: null }
  );

  return (
    <>
      <FormRow>
        <TextField
          label={t('Name')}
          value={value.label}
          onChange={(ev) => onChange({ ...value, label: ev.target.value })}
          fullWidth
        />
        <TextField
          label={t('Data key')}
          value={value.key || camelCase(value.label)}
          disabled={restrictEdits}
          onChange={(ev) => onChange({ ...value, key: ev.target.value })}
          fullWidth
        />
      </FormRow>
      <FormRow>
        <TypeSelector value={value} onChange={onChange} restrictEdits={restrictEdits} />
      </FormRow>

      {value.options.map((option, i) => (
        <div key={i} className={SmartTextOptionContainer}>
          <div className={SmartTextFieldValueRow}>
            <span className={SmartTextFieldValueLabel}>
              Field value is:
            </span>
            <TextField
              placeholder={t('Enter field value')}
              value={option.value}
              disabled={restrictEdits}
              onChange={(ev) => {
                const nextOptions = [...value.options];
                nextOptions[i] = { ...option, value: ev.target.value };
                onChange({ ...value, options: nextOptions });
              }}
              variant="outlined"
              fullWidth
            />
            {i !== 0 && (
              <IconButton
                size="small"
                disabled={restrictEdits}
                onClick={() => {
                  const nextOptions = [...value.options];
                  nextOptions.splice(i, 1);
                  onChange({ ...value, options: nextOptions });
                }}
              >
                <X size={14} />
              </IconButton>
            )}
          </div>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
            {t('When the following conditions are met')}
          </Typography>
          <RuleBuilder
            value={option.rule}
            onChange={(nextRule) => {
              if (nextRule) {
                const nextOptions = [...value.options];
                nextOptions[i] = { ...option, rule: nextRule };
                onChange({ ...value, options: nextOptions });
              }
            }}
            config={ruleConfig}
            variant="compact"
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
            <Button
              size="small"
              color="primary"
              startIcon={<Eye size={14} />}
              onClick={() => setPreviewOptionIndex(i)}
            >
              {t('Preview')}
            </Button>
          </div>
        </div>
      ))}

      <div style={{ display: 'flex', gap: 24, marginBottom: 16 }}>
        <Button
          size="small"
          color="primary"
          startIcon={<PlusCircle size={16} />}
          disabled={restrictEdits}
          onClick={() => {
            onChange({
              ...value,
              options: [
                ...value.options,
                { value: '', rule: EMPTY_SMART_TEXT_RULE }
              ]
            });
          }}
        >
          {t('Add another option')}
        </Button>
        {value.defaultValue === undefined && (
          <Button
            size="small"
            color="primary"
            startIcon={<PlusCircle size={16} />}
            disabled={restrictEdits}
            onClick={() => onChange({ ...value, defaultValue: '' })}
          >
            {t('Add a default, for when no other option matches')}
          </Button>
        )}
      </div>

      {value.defaultValue !== undefined && (
        <div className={SmartTextOptionContainer}>
          <div className={SmartTextOptionHeader}>
            <div className={SmartTextFieldValueRow}>
              <span className={SmartTextFieldValueLabel}>
                Default field value:
              </span>
              <TextField
                placeholder={t('Enter default value')}
                value={value.defaultValue}
                disabled={restrictEdits}
                onChange={(ev) => onChange({ ...value, defaultValue: ev.target.value })}
                variant="outlined"
                fullWidth
              />
            </div>
            <IconButton
              size="small"
              disabled={restrictEdits}
              onClick={() => {
                const { defaultValue, ...rest } = value;
                onChange(rest as CustomFieldSmartText);
              }}
            >
              <X size={14} />
            </IconButton>
          </div>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
            {t('When none of the conditions above are met')}
          </Typography>
        </div>
      )}

      <FormRow>
        <TextField
          label={t('Description')}
          value={value.description}
          rows={3}
          multiline
          onChange={(ev) => onChange({ ...value, description: ev.target.value })}
          fullWidth
        />
      </FormRow>

      <Dialog
        maxWidth="md"
        fullWidth
        open={previewOptionIndex !== null}
        onClose={() => setPreviewOptionIndex(null)}
      >
        <MatchingUsers
          users={users}
          rule={previewOptionIndex !== null ? value.options[previewOptionIndex]?.rule ?? null : null}
          loading={usersLoading}
          onClose={() => setPreviewOptionIndex(null)}
        />
        <DialogActions>
          <Button onClick={() => setPreviewOptionIndex(null)} color="primary">
            {t('Close')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const FieldForm = ({
  value,
  onChange,
  restrictEdits
}: {
  value: CustomUserField;
  onChange: (nextValue: CustomUserField) => void;
  restrictEdits?: boolean;
}) => {
  if (value.type === 'relationship') {
    return (
      <FieldFormRelationship
        value={value}
        onChange={onChange}
        restrictEdits={restrictEdits}
      />
    );
  }
  if (value.type === 'smartDate') {
    return (
      <FieldFormSmartDate
        value={value}
        onChange={onChange}
        restrictEdits={restrictEdits}
      />
    );
  }
  if (value.type === 'smartText') {
    return (
      <FieldFormSmartText
        value={value}
        onChange={onChange}
        restrictEdits={restrictEdits}
      />
    );
  }
  return (
    <FieldFormCommon
      value={value}
      onChange={onChange}
      restrictEdits={restrictEdits}
    />
  );
};

const updateCustomUserFields = (
  orgId: string,
  nextFields: CustomUserField[]
) => {
  const partialDoc: Doc<Pick<IOrganization, 'customUserFields'>> = {
    id: orgId,
    collection: FS.orgs,
    data: { customUserFields: nextFields }
  };
  return updateDocFromPartial(partialDoc);
};

export const AddFieldDialog = ({
  open,
  onClose,
  onCreate,
  orgId,
  otherFields
}: {
  orgId: string;
  otherFields: CustomUserField[];
  open: boolean;
  onClose: () => void;
  onCreate?: (field: CustomUserField) => void;
}) => {
  const { enqueueSnackbarError } = useSnackbar();
  const { t } = useUnsafeTranslation();
  const [field, setField] = useState<CustomUserField>({
    key: '',
    label: '',
    description: '',
    restricted: false,
    type: 'string'
  });
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>
        {t('Add custom data field')}
      </DialogTitleWithTypography>
      <DialogContent>
        <FieldForm value={field} onChange={setField} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color='primary'>
          {t('Cancel')}
        </Button>
        <ButtonWithPromise
          disabled={hasMissingFields(field)}
          pending={`${t('Adding')}...`}
          variant="contained"
          color="primary"
          onClick={async () => {
            const fieldWithDefaultKey = {
              ...field,
              key: field.key || camelCase(field.label)
            };
            if (!isKeyUnique(fieldWithDefaultKey, otherFields)) {
              enqueueSnackbarError(
                lightMdToJsx(
                  t(
                    'The data key **{{ dataKey }}** is already in use. Please provide a different one.'
                  )
                )
              );
              return;
            }
            if (fieldWithDefaultKey.type === 'smartText') {
              const cyclePath = findSmartTextCyclePath(
                fieldWithDefaultKey,
                [...otherFields, fieldWithDefaultKey]
              );
              if (cyclePath) {
                console.error('[SmartText] Cycle detected on add', {
                  fieldKey: fieldWithDefaultKey.key,
                  fieldLabel: fieldWithDefaultKey.label,
                  cyclePath
                });
                enqueueSnackbarError(
                  lightMdToJsx(
                    t(
                      'Cannot save: this creates a cycle (**{{ path }}**). Smart text fields cannot reference themselves directly or indirectly.',
                      { path: cyclePath.join(' → ') }
                    )
                  )
                );
                return;
              }
            }
            const nextFields = [...otherFields, fieldWithDefaultKey];
            await updateCustomUserFields(orgId, nextFields);
            onCreate?.(fieldWithDefaultKey);
            setField({
              key: '',
              label: '',
              description: '',
              restricted: false,
              type: 'string'
            });
            onClose();
          }}
        >
          {t('Add')}
        </ButtonWithPromise>
      </DialogActions>
    </Dialog>
  );
};

const EditFieldDialog = ({
  value,
  orgId,
  otherFields,
  open,
  onClose
}: {
  value: CustomUserField;
  orgId: string;
  otherFields: CustomUserField[];
  open: boolean;
  onClose: () => void;
}) => {
  const { t } = useUnsafeTranslation();
  const { enqueueSnackbarError } = useSnackbar();
  const [field, setField] = useState<CustomUserField>(value);
  useEffect(() => {
    setField(value);
  }, [value]);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>
        {t('Edit custom data field')}
      </DialogTitleWithTypography>
      <DialogContent>
        <FieldForm value={field} onChange={setField} />
      </DialogContent>
      <DialogActionsWithSlots
        left={
          <DeleteButton
            keyword={field.key}
            variant="contained"
            color="secondary"
            dialogTitle={t('Delete data field') + '?'}
            buttonAction={t('Delete field')}
            onDelete={async () => {
              await updateCustomUserFields(
                orgId,
                otherFields.filter((f) => f.key !== field.key)
              );
              onClose();
            }}
          >
            {t('Delete field')}
          </DeleteButton>
        }
        right={
          <>
            <Button onClick={onClose} color='primary'>{t('Cancel')}</Button>
            <ButtonWithPromise
              disabled={hasMissingFields(field)}
              pending={`${t('Editing')}...`}
              variant="contained"
              color="primary"
              onClick={async () => {
                const i = otherFields.findIndex((x) => x.key === field.key);
                if (i === -1) {
                  // should never happen
                  onClose();
                  return;
                }

                if (field.type === 'smartText') {
                  const cyclePath = findSmartTextCyclePath(
                    field,
                    otherFields
                  );
                  if (cyclePath) {
                    console.error('[SmartText] Cycle detected on edit', {
                      fieldKey: field.key,
                      fieldLabel: field.label,
                      cyclePath
                    });
                    enqueueSnackbarError(
                      lightMdToJsx(
                        t(
                          'Cannot save: this creates a cycle (**{{ path }}**). Smart text fields cannot reference themselves directly or indirectly.',
                          { path: cyclePath.join(' → ') }
                        )
                      )
                    );
                    return;
                  }
                }

                const nextFields = [...otherFields];
                nextFields[i] = field;
                await updateCustomUserFields(orgId, nextFields);
                onClose();
              }}
            >
              {t('Save')}
            </ButtonWithPromise>
          </>
        }
      />
    </Dialog>
  );
};

const ContentOrgUserCustomFields = () => {
  const { t } = useUnsafeTranslation();
  const { org } = useCurrentUser();
  const { customUserFields } = org;
  const sideNav = useOrgSideNav();

  const add = useDialogState();
  const [action] = useStringQueryParam('action');
  useEffect(() => {
    if (action === 'add') {
      add.openDialog();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [editedField, setEditedField] = useState<CustomUserField | null>(null);
  const { sortBy, direction, setSort } = useSort<ItemSorter<D, O>>(
    SORTERS.label,
    SORTERS.label.items.dir
  );

  const fields = useMemo(
    () => [
      ...customUserFields,
      ...calculatedFieldsToCustomUserFields(CALCULATED_FIELDS, t),
      ...DEFAULT_USER_FIELDS
    ],
    [customUserFields, t]
  );

  return (
    <PageBody noTopPadding sideNav={sideNav}>
      <PageToolbar sticky>
        <PageToolbarTitle>
          <Typography component="h1" variant="h1">
            {t('pageTitle.orgUsersCustomFields')}
          </Typography>
        </PageToolbarTitle>
        <PageToolbarActions>
          <Button
            variant="contained"
            color="primary"
            onClick={add.openDialog}
            startIcon={<Plus size={16} />}
          >
            {t('Add field')}
          </Button>
        </PageToolbarActions>
      </PageToolbar>
      <Section>
        {!fields.length && (
          <Centered height={200}>{t('No fields defined yet')}.</Centered>
        )}
        {!!fields.length && (
          <RowsRenderer
            variant="contained"
            columns={COLUMNS}
            rows={fields}
            rowToKey={ROW_TO_KEY}
            rowToClassName={(d) =>
              d.restricted ? css({ '&&': { cursor: 'default' } }) : ''
            }
            onRowClick={(d) => {
              if (!d.restricted) {
                setEditedField(d);
              }
            }}
            sorter={sortBy}
            sortDirection={direction}
            renderHead={true}
            headProps={{ sticky: true, offset: 124 }}
            onHeadClick={(c, dir) => {
              const sorter = SORTERS[c.key];
              if (!sorter) {
                return;
              }
              setSort(sorter, dir || sorter.items.dir);
            }}
            chunkSize={30}
            rootMargin="300px"
            rowHeight={ROW_HEIGHTS.dense}
            cellProps={{ adjustHeight: true }}
            otherProps={undefined}
          />
        )}
      </Section>

      <AddFieldDialog
        open={add.dialogOpen}
        onClose={add.closeDialog}
        otherFields={customUserFields}
        orgId={org.id}
      />
      {editedField && (
        <EditFieldDialog
          open={true}
          onClose={() => setEditedField(null)}
          value={editedField}
          otherFields={customUserFields}
          orgId={org.id}
        />
      )}
    </PageBody>
  );
};

export const PageOrgUsersCustomFields = () => {
  const currentUser = useCurrentUser();
  const { ROUTES } = useRoutes();

  if (!hasPermissionToAccessPage(currentUser, ROUTES.org.customFields)) {
    return <NoAccess />;
  }

  return <ContentOrgUserCustomFields />;
};
